import {
  InteractiveContainer,
  ListBoxItem,
  ListBoxItemComponent,
  ResourcePickerPopover,
} from "@jugl-web/ui-kit";
import { cx } from "@jugl-web/utils";
import { FC, useMemo } from "react";
import { ReactComponent as ChevronDownIcon } from "./assets/chevron-down.svg";

type TimezoneType = "business" | "client";

interface TimezoneListItem {
  title: string;
  subtitle: string;
  tzType: TimezoneType;
}

interface TimezoneSelectProps {
  clientTimezoneCode: string;
  businessTimezoneCode: string;
  selectedTimezoneType: TimezoneType;
  onSelect: (timezoneType: "business" | "client") => void;
}

export const TimezoneSelect: FC<TimezoneSelectProps> = ({
  clientTimezoneCode,
  businessTimezoneCode,
  selectedTimezoneType,
  onSelect,
}) => {
  const selectedTimezoneCode =
    selectedTimezoneType === "business"
      ? businessTimezoneCode
      : clientTimezoneCode;

  const items = useMemo<ListBoxItem<TimezoneListItem>[]>(
    () => [
      {
        id: "client",
        value: {
          title: getTimezoneLabel(clientTimezoneCode),
          subtitle: "Your local timezone",
          tzType: "client",
        },
      },
      {
        id: "business",
        value: {
          title: getTimezoneLabel(businessTimezoneCode),
          subtitle: "Business timezone",
          tzType: "business",
        },
      },
    ],
    [businessTimezoneCode, clientTimezoneCode]
  );

  return (
    <ResourcePickerPopover
      placement="bottom-start"
      items={items}
      selectionBehavior={{ mode: "single", canToggle: false }}
      defaultSelectedIds={[selectedTimezoneType]}
      itemSize="2xl"
      renderCustomItem={(item, _, props) => (
        <ListBoxItemComponent
          {...props}
          buttonType="radio"
          className={cx(props.className, "h-[70px]")}
          label={
            <span className="font-secondary text-sm leading-[150%] font-medium text-dark">
              {item.value.title}
            </span>
          }
          secondaryText={
            <span className="font-secondary text-sm leading-[150%] font-normal text-dark-600">
              {item.value.subtitle}
            </span>
          }
        />
      )}
      renderTrigger={({ Trigger, triggerRef, isOpen }) => (
        <Trigger
          ref={triggerRef}
          as={InteractiveContainer}
          className="flex max-w-[170px] items-center gap-1 overflow-hidden"
        >
          <span className="truncate font-secondary text-xs leading-[140%] font-normal text-dark-600">
            {getTimezoneLabel(selectedTimezoneCode)}
          </span>
          <ChevronDownIcon
            className={cx(
              "shrink-0 transition-transform",
              isOpen && "rotate-180"
            )}
          />
        </Trigger>
      )}
      onSelect={({ item, onClose }) => {
        onSelect(item.value.tzType);
        onClose();
      }}
    />
  );
};

const getTimezoneLabel = (timezone: string, locale = "en-US") => {
  const formatter = new Intl.DateTimeFormat(locale, {
    timeZone: timezone,
    timeZoneName: "longGeneric",
  });

  return (
    formatter
      .formatToParts(new Date())
      .find((part) => part.type === "timeZoneName")?.value ?? timezone
  );
};
